import { EyeIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import parse from 'html-react-parser';
import { isEmpty } from 'radash';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Callout } from '@/components/core/Callout';
import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { Table, TableBody, TableCell, TableRow } from '@/components/core/Table';
import { AttributeComponent } from '@/components/datamodel/AttributeComponent';
import { LayoutProvider } from '@/components/datamodel/hooks/useLayout';
import { useTranslation } from '@/hooks/useTranslation';
import { DataModelAttribute } from '@/types/DataModel';

import {
    isQueryBasedSelection,
    MassUpdateAction,
    MassUpdateAttributeValue,
} from '../mass-update.types';

/**
 *
 * Step 3
 */
const Summary = ({
    selectedAttributes,
    updatedValues,
    primaryKeys,
}: {
    selectedAttributes: DataModelAttribute[];
    updatedValues: Record<string, MassUpdateAttributeValue>;
    primaryKeys: string[] | undefined;
}) => {
    const { t } = useTranslation();
    const { getValues } = useFormContext();

    const [editedAttribute, setEditedAttribute] = useState<
        DataModelAttribute[]
    >([]);
    const [clearedAttribute, setClearedAttribute] = useState<
        DataModelAttribute[]
    >([]);

    const queryBasedSelection = isQueryBasedSelection(primaryKeys);

    useEffect(() => {
        const values = getValues();
        const listEditedAttribute: DataModelAttribute[] = [];
        const listClearedAttribute: DataModelAttribute[] = [];

        Object.keys(values).forEach((key) => {
            const a = selectedAttributes.find((att) => att.name === key);
            updatedValues[key].value = values[key];
            if (a) {
                if (
                    updatedValues[key].action ===
                        MassUpdateAction.CLEAR ||
                    isEmpty(updatedValues[key].value)
                ) {
                    listClearedAttribute.push(a);
                } else {
                    listEditedAttribute.push(a);
                }
            }
        });

        setEditedAttribute(listEditedAttribute);
        setClearedAttribute(listClearedAttribute);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LayoutProvider layoutName="mass_update">
            {!queryBasedSelection && (
                <Callout className="tw-my-6" variant="info">
                    {t('MASS_UPDATE_EDITOR.SUMMARY.DESCRIPTION')}
                </Callout>
            )}
            <span className="tw-font-bold tw-my-6">
                {t('MASS_UPDATE_EDITOR.SUMMARY.TEXT')}
            </span>
            <div className="tw-my-2">
                {parse(
                    queryBasedSelection
                        ? t('MASS_UPDATE_EDITOR.SUMMARY.SELECTION_ALL_SYSTEM')
                        : t('MASS_UPDATE_EDITOR.SUMMARY.SELECTION_NUMBER', {
                            size: (primaryKeys as string[]).length,
                        }),
                )}
            </div>
            <div className="tw-overflow-auto tw-h-[65%] tw-max-w-screen">
                <div className="tw-flex tw-flex-col tw-my-6">
                    {primaryKeys instanceof Array &&
                    <Table className="tw-table-fixed">
                        <TableBody>
                            { primaryKeys.map((primaryKey, index) => (
                                <TableRow
                                    key={index}
                                    className="odd:tw-bg-white even:tw-bg-gray-100"
                                >
                                    <TableCell className="tw-w-[10px] tw-bg-transparent">
                                        {index + 1}
                                    </TableCell>
                                    <TableCell className="tw-w-full tw-bg-transparent">
                                        <div className="tw-flex tw-flex-row">
                                            <span className="tw-font-bold tw-mx-2">
                                                {t('attribute.primaryKey__')} :
                                            </span>
                                            <div>{primaryKey}</div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="tw-w-[100px] tw-bg-transparent">
                                        <IconButton
                                            type="button"
                                            className="tw-mr-1 text-xs"
                                            icon={EyeIcon}
                                            onClick={() => {
                                                window.open(
                                                    `/items/detail/${primaryKey}`,
                                                    '_blank',
                                                    'noopener,noreferrer',
                                                );
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>}
                </div>
                <div className="tw-mt-5 tw-w-full tw-py-4">
                    <div className="tw-my-2">
                        <div>
                            {editedAttribute.length > 0 && (
                                <div>
                                    <div className="tw-flex tw-items-center tw-my-4 tw-text-[--secondary]">
                                        <Icon
                                            size="sm"
                                            icon={PencilSquareIcon}
                                            variant='secondary'
                                        />
                                        <div className="tw-ml-2 tw-w-30"> {t('MASS_UPDATE_EDITOR.EDITED_LABEL')} </div>
                                        <div className="tw-flex-grow tw-w-70 tw-border-l-0 tw-border-r-0 tw-border-b-0 tw-border-t-1 tw-border-solid tw-border-[--light-gray] tw-ml-2"></div>
                                    </div>
                                    {editedAttribute.map((attribute, index) => (
                                        <AttributeComponent
                                            key={index}
                                            attribute={attribute}
                                            readonly={true}
                                            disableUrlSync={true}
                                        />
                                    ))}
                                </div>
                            )}
                            {clearedAttribute.length > 0 && (
                                <div className="tw-w-full">
                                    <div className="tw-flex tw-items-center tw-my-4 tw-text-[--error-dark]">
                                        <Icon
                                            size="sm"
                                            icon={TrashIcon}
                                            variant='destructive'
                                        />
                                        <div className="tw-ml-2 tw-w-30"> {t('MASS_UPDATE_EDITOR.CLEARED_LABEL')} </div>
                                        <div className="tw-flex-grow tw-w-70 tw-border-l-0 tw-border-r-0 tw-border-b-0 tw-border-t-1 tw-border-solid tw-border-[--light-gray] tw-ml-2"></div>
                                    </div>
                                    <div className="tw-w-full">
                                        {clearedAttribute.map(
                                            (attribute, index) => (
                                                <AttributeComponent
                                                    key={index}
                                                    attribute={attribute}
                                                    readonly={true}
                                                    disableUrlSync={true}
                                                />
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </LayoutProvider>
    );
};

export default Summary;
